/**
 * @file ChatHint.tsx
 * @description Подсказка над кнопкой чата (chat-window-design.md §1а, §7):
 * «Новое сообщение · Заказ Z-…», «Диалог передан вам · Анна Кравец»,
 * «Диалог вернулся из архива · …». Исчезает через 6 с, на наведении и фокусе
 * — пауза. Нажатие открывает окно на диалоге.
 */

import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import { CHAT_TEXT } from "./chat-texts";
import { HINT_TIMEOUT_MS, hintTitle } from "./launcher-view";
import type { ChatHint as ChatHintData } from "./useChatSummary";

interface Props {
  hint: ChatHintData;
  /** Нижний край подсказки, px от низа экрана. */
  bottom: number;
  onOpen: (conversationId: string) => void;
  onDismiss: () => void;
}

export function ChatHint({ hint, bottom, onOpen, onDismiss }: Props) {
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(onDismiss, HINT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [paused, hint, onDismiss]);

  return (
    <div
      role="status"
      className="fixed right-4 z-[var(--z-chat)] w-[calc(100vw-2rem)] sm:max-w-80 lg:right-6"
      style={{ bottom }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="relative rounded-xl bg-surface-lowest [box-shadow:0_12px_32px_var(--shadow-ambient),inset_0_0_0_1px_var(--outline-variant)]">
        <button
          type="button"
          onClick={() => onOpen(hint.conversationId)}
          className="flex w-full flex-col gap-0.5 rounded-xl py-3 pl-4 pr-14 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring lg:pr-11"
        >
          <span className="flex items-center gap-2 text-xs font-medium text-on-surface-variant">
            <span aria-hidden className="size-2 shrink-0 rounded-full bg-primary" />
            <span className="truncate">{hintTitle(hint)}</span>
          </span>
          {hint.customerName ? <span className="truncate text-sm font-semibold text-on-surface">{hint.customerName}</span> : null}
          {hint.preview && hint.kind === "MESSAGE" ? (
            <span className="line-clamp-2 text-sm text-on-surface-variant">{hint.preview}</span>
          ) : null}
          {hint.more > 0 ? <span className="text-xs text-on-surface-variant">{CHAT_TEXT.hintMore(hint.more)}</span> : null}
        </button>
        <button
          type="button"
          aria-label={CHAT_TEXT.hintClose}
          onClick={onDismiss}
          className={cn(
            "absolute right-0 top-0 inline-flex items-center justify-center rounded-xl text-on-surface-variant outline-none",
            "size-12 hover:text-on-surface focus-visible:ring-2 focus-visible:ring-ring lg:size-8 lg:right-1 lg:top-1",
          )}
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
